import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchAndCachePdf, getCachedPdfUrl } from './pdfCache';

export type PdfFileSource = { url: string; withCredentials: boolean } | { data: ArrayBuffer };

interface UsePdfViewerProps {
	fileUrl: string;
	isOpen?: boolean;
	/** Page to show first — used so a cited source preview opens at the cited page (1-based). */
	initialPage?: number;
}

export type PageNumberReasonChange = 'control' | 'scroll' | null;

interface UsePdfViewerReturn {
	numPages: number;
	pageNumber: number;
	loading: boolean;
	pdfOptions: PdfFileSource;
	onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
	onDocumentLoadError: (error: Error) => void;
	goToPrevPage: () => void;
	goToNextPage: () => void;
	handlePageNumberChange: (page: number, reason?: PageNumberReasonChange) => void;
	openInNewTab: () => void;
	setPageChangeReason: (callback: (reason: PageNumberReasonChange) => void) => void;
}
/**
 * Custom hook for managing PDF viewer state and common operations
 * Provides reusable state management for PDF components
 */
export const usePdfViewer = ({ fileUrl, isOpen, initialPage = 1 }: UsePdfViewerProps): UsePdfViewerReturn => {
	const [numPages, setNumPages] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(initialPage);
	const [loading, setLoading] = useState<boolean>(true);

	// Callback to be set by the scroll sync hook
	const setPageChangeReasonRef = useRef<((reason: PageNumberReasonChange) => void) | null>(null);

	// Reset to the requested starting page when the dialog opens.
	useEffect(() => {
		if (isOpen) {
			setPageNumber(initialPage);
		}
	}, [initialPage, isOpen]);

	useEffect(() => {
		setPageNumber(Math.max(1, Math.min(initialPage, numPages || initialPage)));
	}, [initialPage, numPages]);

	/**
	 * Resolved URL for the PDF — either a cached blob URL or the original fileUrl.
	 *
	 * The URL is resolved once at mount (or when fileUrl changes) and never updated
	 * mid-view. This prevents PDF.js from destroying its worker transport while
	 * <Page> components still reference it, which causes the "sendWithPromise" error.
	 * The background fetch only populates the cache for future opens.
	 */
	const resolvedUrl = useMemo(() => getCachedPdfUrl(fileUrl) ?? fileUrl, [fileUrl]);

	// Background-fetch into cache for future opens — never updates resolvedUrl
	useEffect(() => {
		if (getCachedPdfUrl(fileUrl)) return;
		let cancelled = false;
		fetchAndCachePdf(fileUrl).catch(() => {
			if (!cancelled) {
				/* silently ignore cache failures */
			}
		});
		return () => {
			cancelled = true;
		};
	}, [fileUrl]);

	const pdfOptions: PdfFileSource = useMemo(() => ({ url: resolvedUrl, withCredentials: false }), [resolvedUrl]);

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
		// Clamp the requested initial page to the document's range once we know how many pages it has.
		setPageNumber(Math.max(1, Math.min(initialPage, numPages)));
		setLoading(false);
	};

	const onDocumentLoadError = (error: Error) => {
		console.error('Error loading PDF:', error);
		setLoading(false);
	};

	const goToPrevPage = () => {
		const newPage = Math.max(pageNumber - 1, 1);
		setPageNumber(newPage);
		// Notify scroll sync that this was a control action
		if (setPageChangeReasonRef.current) {
			setPageChangeReasonRef.current('control');
		}
	};

	const goToNextPage = () => {
		const newPage = Math.min(pageNumber + 1, numPages);
		setPageNumber(newPage);
		// Notify scroll sync that this was a control action
		if (setPageChangeReasonRef.current) {
			setPageChangeReasonRef.current('control');
		}
	};

	const handlePageNumberChange = (page: number, reason: PageNumberReasonChange = 'control') => {
		setPageNumber(Math.max(1, Math.min(page, numPages)));
		// Notify scroll sync about the reason
		if (setPageChangeReasonRef.current) {
			setPageChangeReasonRef.current(reason);
		}
	};

	const setPageChangeReason = (callback: (reason: PageNumberReasonChange) => void) => {
		setPageChangeReasonRef.current = callback;
	};

	const openInNewTab = () => {
		window.open(fileUrl, '_blank', 'noopener,noreferrer');
	};

	return {
		numPages,
		pageNumber,
		loading,
		pdfOptions,
		onDocumentLoadSuccess,
		onDocumentLoadError,
		goToPrevPage,
		goToNextPage,
		handlePageNumberChange: handlePageNumberChange,
		openInNewTab,
		setPageChangeReason,
	};
};
